import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { getContentTypeIcon } from '@classification-testing/utils';
import { getLanguageDisplayInfo, getLanguageName } from '@classification-testing/utils/languageHelpers';
import { formatTimestamp } from '@core/utils';
import { IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock, Icon, Badge, Dialog } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultDialog.css';
import { ContentDisplaySection, ImageDisplaySection } from './ClassificationResultDialogContentSections';
import { PoliciesSection } from './PolicyComponents';

// Type for classification label (from ClassificationResult interface)
type ClassificationLabel = ClassificationResult['labels'][0];

interface ClassificationResultDialogProps {
  result: ClassificationResult;
  isOpen: boolean;
  onClose: () => void;
  isSafe: boolean;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}

// Helper function to get dialog title based on content type
function getDialogTitle(contentType: ContentType) {
  switch (contentType) {
    case ContentType.AUDIO:
      return 'Audio Classification Result';
    case ContentType.IMAGE:
      return 'Image Classification Result';
    case ContentType.TEXT:
      return 'Text Classification Result';
    case ContentType.MIXED:
      return 'Mixed Content Classification Result';
    default:
      return 'Classification Result';
  }
}

// Label info component
function LabelInfo({ label }: { label: ClassificationLabel }) {
  return (
    <div className={styles.categoryInfo}>
      <span className={styles.categoryName}>{label.label_name}</span>
      {label.label_short_name && label.label_short_name !== label.label_name && <span className={styles.categoryShortName}>({label.label_short_name})</span>}
    </div>
  );
}

// Helper function to get scope display text
function getScopeDisplayText(scope: ClassificationLabel['scope']): string {
  if (!scope) return '';

  return scope.global ? 'Global' : `Communities: ${scope.communities?.length || 0}`;
}

// Helper to normalize content type for case-insensitive comparison
function normalizeContentType(contentType: string | undefined): string | undefined {
  return contentType?.toLowerCase();
}

// Label details component
function LabelDetails({ label }: { label: ClassificationLabel }) {
  const normalizedType = normalizeContentType(label.content_type);
  const showSeverity = normalizedType === 'text' && label.severity !== undefined;
  const showScore = normalizedType === 'image' && label.score !== undefined;

  return (
    <div className={styles.categoryDetails}>
      {/* Show severity for text labels */}
      {showSeverity && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      )}
      {/* Show score for image labels */}
      {showScore && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Score:</span>
          <span className={styles.severityValue}>{label.score?.toFixed(2)}%</span>
        </div>
      )}
      {label.scope && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Scope:</span>
          <span className={styles.severityValue}>{getScopeDisplayText(label.scope)}</span>
        </div>
      )}
      {label.content_type && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Content Type:</span>
          <span className={styles.severityValue}>{label.content_type}</span>
        </div>
      )}
      {label.metadata && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Policy:</span>
          <span className={styles.severityValue}>{label.metadata.policy}</span>
        </div>
      )}
    </div>
  );
}

// Label item component to reduce complexity
function LabelItem({ label }: { label: ClassificationLabel }) {
  const isSafe = label.label_name.toLowerCase().includes('safe');

  return (
    <div className={`${styles.categoryItem} ${isSafe ? styles.categoryItemSafe : styles.categoryItemUnsafe}`}>
      <LabelInfo label={label} />
      <LabelDetails label={label} />
    </div>
  );
}

// Enhanced Labels section component
function LabelsSection({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      <div className={styles.categoriesList}>
        {result.labels.map(label => (
          <LabelItem key={label.label_id || label.label_name} label={label} />
        ))}
      </div>
    </div>
  );
}

// Categories section component
function CategoriesSection({ result }: { result: ClassificationResult }) {
  if (!result.categories || result.categories.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Categories</h3>
      <div className={styles.categoriesList}>
        {result.categories.map(category => (
          <div key={category} className={styles.categoryItem}>
            <div className={styles.categoryInfo}>
              <span className={styles.categoryName}>{category}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to get score value for display
function getScoreValue(contentType: ContentType, confidenceScore: number | undefined): number {
  if (confidenceScore === undefined) return 0;

  return contentType === ContentType.IMAGE ? confidenceScore : confidenceScore * 100;
}

// Component for rendering confidence/score display
function ConfidenceScoreDisplay({ result }: { result: ClassificationResult }) {
  if (result.confidenceScore === undefined) return null;

  const scoreValue = getScoreValue(result.contentType, result.confidenceScore);
  const label = result.contentType === ContentType.IMAGE ? 'Overall Score:' : 'Overall Confidence:';

  return (
    <div className={styles.metadataItemInline}>
      <span className={styles.metadataLabel}>{label}</span>
      <span className={styles.metadataValue}>{scoreValue.toFixed(4)}%</span>
    </div>
  );
}

// Component for rendering language information
function LanguageDisplay({ result }: { result: ClassificationResult }) {
  const languageInfo = getLanguageDisplayInfo(result.languageDetected, result.languageClassified);

  if (languageInfo.showTranslationIndicator) {
    return (
      <>
        {result.languageDetected && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language Detected:</span>
            <span className={styles.metadataValue}>{getLanguageName(result.languageDetected)}</span>
          </div>
        )}
        {result.languageClassified && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language Classified:</span>
            <span className={styles.metadataValue}>{getLanguageName(result.languageClassified)}</span>
          </div>
        )}
      </>
    );
  }

  if (result.languageClassified || result.languageDetected) {
    return (
      <div className={styles.metadataItemInline}>
        <span className={styles.metadataLabel}>Language:</span>
        <span className={styles.metadataValue}>{getLanguageName(result.languageClassified || result.languageDetected)}</span>
      </div>
    );
  }

  return null;
}

// Enhanced metadata section component
function EnhancedMetadataSection({ result }: { result: ClassificationResult }) {
  const hasEnhancedData = result.confidenceScore !== undefined || result.language || result.languageDetected || result.duration;

  if (!hasEnhancedData) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Classification Metadata</h3>
      <div className={styles.metadataGrid}>
        <ConfidenceScoreDisplay result={result} />
        <LanguageDisplay result={result} />
        {result.duration && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Duration:</span>
            <span className={styles.metadataValue}>{(result.duration / 1000).toFixed(2)}s</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Explanation section component
function ExplanationSection({ result }: { result: ClassificationResult }) {
  if (!result.explanation) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Explanation</h3>
      <div className={styles.contentBox}>
        <span>{result.explanation}</span>
      </div>
    </div>
  );
}

/**
 * Dialog component for displaying classification result details
 */
export function ClassificationResultDialog({ result, isOpen, onClose, isSafe, isContentBlurred, onToggleBlur }: ClassificationResultDialogProps) {
  const dialogTitle = getDialogTitle(result.contentType);
  const dialogDescription = formatTimestamp(result.timestamp);

  const customHeader = (
    <div>
      <div className={styles.titleContainer}>
        <Icon icon={getContentTypeIcon(result.contentType)} iconSize={IconSize.Small} />
        <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
        {!isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
      {dialogDescription && <p className={styles.dialogDescription}>{dialogDescription}</p>}
    </div>
  );

  return (
    <Dialog cancelAction={{ label: 'Close' }} customHeader={customHeader} isOpen={isOpen} size="xl" onClose={onClose}>
      <div className={styles.statusBadge}>
        <Badge className={isSafe ? styles.safeBadge : styles.unsafeBadge} text={isSafe ? 'SAFE' : 'UNSAFE'} />
      </div>
      <div className={styles.container}>
        {/* Image Display */}
        <ImageDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

        {/* Content Display */}
        <ContentDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

        {/* Classification Explanation */}
        <ExplanationSection result={result} />

        {/* Enhanced Metadata */}
        <EnhancedMetadataSection result={result} />

        {/* Categories */}
        <CategoriesSection result={result} />

        {/* Labels */}
        <LabelsSection result={result} />

        {/* Policies */}
        <PoliciesSection result={result} />
      </div>
    </Dialog>
  );
}
